import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  DaterangeConfig,
  IdentifierTriggerComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { ExportFormat, ExportComponent } from '@next/core-lib/export';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import {
  ExportOptionsV2,
  TableComponent,
  TableSearchTemplateDirective,
  TableSearchComponent,
  TableFiltersTemplateDirective,
  TableDateRangeFilterComponent,
  TableMultiSelectFilterComponent,
  TableFilterLocationsDirective,
  TableTopBarTemplateDirective,
  TableTopBarComponent,
  TableSelectedItemsComponent,
  TableExportDirective,
} from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, of, Subscription, take } from 'rxjs';
import { DeliveryDataService } from 'src/app/delivery-data/services/delivery-data.service';
import { printDeliveryReportRequest } from 'src/app/delivery-data/store/delivery-data.actions';
import { DateService } from 'src/app/shared/services/date.service';

import { GreenhouseDataSource } from '../../datasource/greenhouse.datasource';
import {
  displayColumns,
  greenhouseColumns,
  GreenhouseDataFilters,
  GreenhouseExportRow,
  GreenhouseViewModel,
} from '../../models/greenhouse.model';
import { GreenhouseNoticeComponent } from '../greenhouse-notice/greenhouse-notice.component';

@Component({
  selector: 'app-greenhouse-overview',
  templateUrl: './greenhouse-overview.component.html',
  styleUrls: ['./greenhouse-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    GreenhouseNoticeComponent,
    TableComponent,
    MatSort,
    TableSearchTemplateDirective,
    ReactiveFormsModule,
    TableSearchComponent,
    TableFiltersTemplateDirective,
    TableDateRangeFilterComponent,
    TableMultiSelectFilterComponent,
    TableFilterLocationsDirective,
    TableTopBarTemplateDirective,
    TableTopBarComponent,
    TableSelectedItemsComponent,
    ExportComponent,
    TableExportDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    IdentifierTriggerComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    NotificationEmptyComponent,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class GreenhouseOverviewComponent implements OnInit, OnDestroy {
  readonly dataSource = new GreenhouseDataSource(this._deliveryDataService);
  readonly displayColumns = displayColumns;
  readonly exportOptions: ExportOptionsV2;
  readonly dateRangeConfig = this.dateService.createDateConfigWithCalendarYearOptions();
  readonly subscriptions: Subscription[] = [];

  readonly searchAndFilters = new FormGroup({
    search: new FormControl(),
    locations: new FormControl(),
    dateRange: new FormControl(),
  });

  constructor(
    private readonly _deliveryDataService: DeliveryDataService,
    private readonly dateService: DateService,
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
  ) {
    this.exportOptions = {
      formats: [
        {
          format: ExportFormat.xlsx,
          name: 'XLSX',
        },
        {
          format: ExportFormat.csv,
          name: 'CSV',
        },
        {
          name: this.localeService.translate('common.printReport'),
          export: (data: GreenhouseExportRow[]) => this.printReport(data),
        },
      ],
      fileName: this.localeService.translate('greenhouse.greenhouse-overview.fileName'),
      columns: greenhouseColumns.map((column) => ({
        id: column.id,
        name: this.localeService.translate(`greenhouse.greenhouse-overview.${column.id}`),
      })),
    };
  }
  printReport(data: GreenhouseExportRow[]): void {
    combineLatest([
      of(this.searchAndFilters.value as GreenhouseDataFilters),
      this.store.select(UserSelectors.selectCustomer),
      this.dataSource._order$,
    ])
      .pipe(take(1))
      .subscribe(([filters, customer, order]) => {
        const startDate = filters.dateRange.startDate;
        const endDate = filters.dateRange.endDate;
        data.sort((objectA, objectB) => {
          const propertyA = objectA[order.column as keyof GreenhouseExportRow];
          const propertyB = objectB[order.column as keyof GreenhouseExportRow];
          const result = propertyA > propertyB ? 1 : propertyA < propertyB ? -1 : 0;
          return order.descending ? -result : result;
        });

        this.store.dispatch(
          printDeliveryReportRequest({
            report: {
              title: this.localeService.translate('greenhouse.title-details'),
              subTitle: `${this.localeService.translate(
                'greenhouse.report.sub-title',
              )} ${this._deliveryDataService.formatDateTime(new Date())}`,
              fileName: this._deliveryDataService.formatExportFileName(
                this.localeService.translate('greenhouse.greenhouse-overview.fileName'),
                customer.externalId,
                startDate,
                endDate,
              ),
              legend: {
                [this.localeService.translate('greenhouse.report.legend.customer')]:
                  `${customer.name} (${customer.externalId})`,
                [this.localeService.translate('greenhouse.report.legend.start-date')]:
                  this._deliveryDataService.formatDateTime(startDate),
                [this.localeService.translate('greenhouse.report.legend.end-date')]:
                  this._deliveryDataService.formatDateTime(endDate),
              },
              tableHeadings: this.exportOptions.columns.map((col) => col.name) ?? [],
              tableFooter: [],

              contactInformation: {
                email: this.localeService.translate('greenhouse.report.contact.email'),
                phoneNumber: this.localeService.translate('greenhouse.report.contact.phone-number'),
              },
              rows: this._deliveryDataService.mapToPdfRows(greenhouseColumns, data),
            },
          }),
        );
      });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.searchAndFilters.valueChanges.subscribe(() =>
        this.dataSource.applyFilters(this.searchAndFilters.value as GreenhouseDataFilters),
      ),
    );
  }

  resetFilters() {
    this.searchAndFilters.reset({
      search: null,
      locations: null,
      dateRange: new DaterangeConfig().config.initial,
    });
  }

  isEqual(a: GreenhouseViewModel, b: GreenhouseViewModel) {
    return a.id === b.id && a.deliveryId === b.deliveryId;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
